import { useMemo, useState } from "react";
import { GiCrossedSwords } from "react-icons/gi";
import { UnitCatalogEntry, UnitClassIcon, UnitClassRu } from "../types/army";
import { HeroEntry } from "../types/heroes";
import { OpponentEntry } from "../types/marches";
import { formatDuration, formatNumber } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";

interface Props {
  opponent: OpponentEntry;
  garrison: Record<string, number>;
  unitsById: Map<string, UnitCatalogEntry>;
  ownedHeroes: HeroEntry[];
  marchCapacity: number;
  marchSpeedReductionPct: number;
  travelBaseSeconds: number;
  onBack: () => void;
  onConfirm: (units: Record<string, number>, heroIds: string[], stratagem: number) => void;
  pending: boolean;
  error: string | null;
}

// Battle stratagem options. Must match server-side Stratagem enum
// (Balanced=0, Aggressive=1, Defensive=2).
const STRATAGEMS = [
  { id: 0, label: "Сбалансированная", hint: "Без модификаторов" },
  { id: 1, label: "Агрессивная", hint: "+25% Атака, −15% Защита" },
  { id: 2, label: "Защитная", hint: "+25% Защита, −15% Атака" },
];

const presets = [25, 50, 75, 100];

export function AttackComposer({
  opponent,
  garrison,
  unitsById,
  ownedHeroes,
  marchCapacity,
  marchSpeedReductionPct,
  travelBaseSeconds,
  onBack,
  onConfirm,
  pending,
  error,
}: Props) {
  const garrisonRows = useMemo(() => {
    return Object.entries(garrison)
      .map(([id, total]) => ({ id, total, unit: unitsById.get(id) }))
      .filter((r) => r.total > 0)
      .sort((a, b) => (a.unit?.cls ?? 99) - (b.unit?.cls ?? 99));
  }, [garrison, unitsById]);

  const [picked, setPicked] = useState<Record<string, number>>({});
  const topHeroIds = useMemo(
    () =>
      [...ownedHeroes]
        .sort((a, b) => b.level * 5 + b.stars - (a.level * 5 + a.stars))
        .slice(0, 3)
        .map((h) => h.id),
    [ownedHeroes],
  );
  const [heroIds, setHeroIds] = useState<string[]>(topHeroIds);
  const [stratagem, setStratagem] = useState<number>(0);

  const totalPicked = useMemo(
    () => Object.values(picked).reduce((s, v) => s + (v > 0 ? v : 0), 0),
    [picked],
  );

  const setUnit = (id: string, value: number, max: number) => {
    const safe = Math.max(0, Math.min(Math.floor(value || 0), max));
    setPicked((prev) => {
      const next = { ...prev };
      if (safe === 0) delete next[id];
      else next[id] = safe;
      return next;
    });
  };

  const applyPreset = (pct: number) => {
    hapticImpact("light");
    const factor = pct / 100;
    const next: Record<string, number> = {};
    let runningTotal = 0;
    for (const r of garrisonRows) {
      const want = Math.floor(r.total * factor);
      if (want > 0 && runningTotal + want <= marchCapacity) {
        next[r.id] = want;
        runningTotal += want;
      } else if (want > 0) {
        const remain = Math.max(0, marchCapacity - runningTotal);
        if (remain > 0) {
          next[r.id] = remain;
          runningTotal += remain;
          break;
        }
      }
    }
    setPicked(next);
  };

  const toggleHero = (id: string) => {
    setHeroIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const travelTime = Math.ceil(
    travelBaseSeconds * (1 - Math.min(marchSpeedReductionPct, 0.9)),
  );

  const canSubmit = totalPicked > 0 && totalPicked <= marchCapacity && !pending;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
          >
            ←
          </button>
          <div>
            <div className="font-medium">Атака</div>
            <div className="text-xs text-tg-hint">→ {opponent.displayName}</div>
          </div>
        </div>
        <div className="text-right text-xs">
          <div className="text-tg-hint">Войск выбрано</div>
          <div className={`tabular-nums ${totalPicked > marchCapacity ? "text-tg-destructive" : ""}`}>
            {formatNumber(totalPicked)} / {formatNumber(marchCapacity)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 px-3 pb-2">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => applyPreset(p)}
            className="rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm"
          >
            {p}%
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            hapticImpact("light");
            setPicked({});
          }}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm text-tg-hint"
        >
          сброс
        </button>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {garrisonRows.length === 0 ? (
          <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
            Гарнизон пуст. Сначала найми войска во вкладке «Армия».
          </div>
        ) : (
          garrisonRows.map((r) => {
            const max = r.total;
            const value = picked[r.id] ?? 0;
            return (
              <div key={r.id} className="rounded-xl bg-tg-secondaryBg p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-2xl">
                    {r.unit ? UnitClassIcon[r.unit.cls] : "❓"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {r.unit?.name ?? r.id}
                    </div>
                    <div className="text-xs text-tg-hint">
                      {r.unit ? UnitClassRu[r.unit.cls] : ""} · {formatNumber(max)} в гарнизоне
                    </div>
                  </div>
                </div>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={max}
                  value={value > 0 ? value : ""}
                  placeholder={`0 / ${max}`}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setUnit(r.id, 0, max);
                      return;
                    }
                    const n = parseInt(raw, 10);
                    if (!Number.isFinite(n)) return;
                    setUnit(r.id, n, max);
                  }}
                  className="w-full rounded-lg bg-tg-bg p-1.5 text-center tabular-nums"
                />
              </div>
            );
          })
        )}

        {ownedHeroes.length > 0 && (
          <div className="rounded-xl bg-tg-secondaryBg p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-tg-hint">
              Герои (до 3) — выбрано {heroIds.length}/3
            </div>
            <div className="space-y-1">
              {ownedHeroes.map((h) => {
                const selected = heroIds.includes(h.id);
                const passiveSummary = h.passives?.[0]?.summary;
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => toggleHero(h.id)}
                    className={`flex w-full items-center justify-between rounded-lg p-2 text-left text-sm ${
                      selected ? "bg-tg-button text-tg-buttonText" : "bg-tg-bg"
                    }`}
                  >
                    <div>
                      <div className="font-medium">{h.name}</div>
                      <div className="text-xs opacity-80">
                        Lvl {h.level} · {"⭐".repeat(h.stars)}
                        {passiveSummary ? ` · ${passiveSummary}` : ""}
                      </div>
                    </div>
                    <div>{selected ? "✓" : ""}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="rounded-xl bg-tg-secondaryBg p-3">
          <div className="mb-2 text-xs uppercase tracking-wide text-tg-hint">
            Стратагема
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {STRATAGEMS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  hapticImpact("light");
                  setStratagem(s.id);
                }}
                className={`flex flex-col items-center gap-0.5 rounded-lg px-2 py-2 text-[11px] leading-tight transition-colors ${
                  stratagem === s.id
                    ? "bg-tg-button text-tg-buttonText"
                    : "bg-tg-bg/40 text-tg-hint"
                }`}
              >
                <span className="font-semibold">{s.label}</span>
                <span className="text-[9px] opacity-80">{s.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-tg-secondaryBg p-3 text-xs text-tg-hint">
          ⏱ Путь: {formatDuration(travelTime)}
          {marchSpeedReductionPct > 0 && (
            <span className="ml-1 text-tg-accent">
              (−{Math.round(marchSpeedReductionPct * 100)}%)
            </span>
          )}
        </div>

        {error && (
          <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {error}
          </div>
        )}
      </div>

      <div className="border-t border-tg-secondaryBg p-3">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            hapticImpact("medium");
            onConfirm(picked, heroIds, stratagem);
            hapticNotification("success");
          }}
          className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
        >
          {pending ? "Отправляю…" : (
            <span className="inline-flex items-center justify-center gap-1.5">
              <GiCrossedSwords size={16} /> Отправить атаку
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
